import { useMemo } from 'react';
import { useCommissionRules, useCommissionRulesRenovacaoDelta, useMetasConfig, useMetaMensal, encontrarFaixa, encontrarFaixaSupervisor, FAIXAS_FATURAMENTO } from './useCommissionRules';
import { PERCENTUAIS_EXPERIENCIA } from './regraExperiencia';
import { calcularComissaoRenovacaoDelta, type ComissaoRenovacaoDeltaDetalhe, type ItemComRenovacaoDelta } from '@/lib/comissaoRenovacaoDelta';

import { renovacaoBatidaPorFibra } from '../../../../backend/src/domain/comissao/renovacaoPorFibra';
import { fatorMigracaoJunta, metaBatidaPorMigracao, metaMigracaoJunta } from '../../../../backend/src/domain/comissao/migracaoJunta';
import { calcularFaturamentoPorCategoria, baseComissionavel, type CategoriasComissaoConfig, type ResultadoComissao, type FaturamentoPorCategoria, type QuantidadesMetas } from '../../../../backend/src/domain/comissao/faturamento';
export * from '../../../../backend/src/domain/comissao/faturamento';

export interface MetaAgregadaGestor {
  meta_altas: number; meta_fibras: number; meta_fat_vn: number; meta_fat_total: number;
  meta_qtd_tt: number; meta_qtd_mp: number; meta_qtd_migracao: number;
  meta_fat_ti: number; meta_fat_avancada: number;
  meta_sd: number;
  meta_tv?: number;
  usar_meta_altas: boolean; usar_meta_fibras: boolean; usar_meta_fat_vn: boolean;
  usar_meta_fat_total: boolean; usar_meta_qtd_tt: boolean; usar_meta_qtd_mp: boolean;
  usar_meta_qtd_migracao: boolean;
  usar_meta_fat_ti: boolean; usar_meta_fat_avancada: boolean;
  usar_meta_sd: boolean;
  usar_meta_tv?: boolean;
}

export function useCalculoComissao(
  cargo: 'CONSULTOR' | 'SUPERVISOR' | 'COORDENADOR' | 'GERENTE',
  faturamentos: FaturamentoPorCategoria,
  quantidades: QuantidadesMetas,
  nVendedores: number = 0,
  competencia: Date = new Date(),
  /** Quantidades usadas APENAS no check bateuMeta (inclui DIRETORIA). Se omitido, usa `quantidades`. */
  quantidadesMeta?: QuantidadesMetas,
  /** Faturamentos usados APENAS no check bateuMeta (inclui DIRETORIA). Se omitido, usa `faturamentos`. */
  faturamentosMeta?: FaturamentoPorCategoria,
  /** usuarioId opcional para buscar regras individuais (supervisores) */
  usuarioId?: string,
  /**
   * Quando true E `cargo === 'CONSULTOR'`, aplica a regra MÍNIMA de período de
   * experiência: comissão liberada sem precisar bater meta, percentuais
   * `PERCENTUAIS_EXPERIENCIA` (50/50/50/10/10). Se bateuMeta=true E a comissão
   * normal for maior, prevalece a maior.
   * Default `false` mantém comportamento idêntico ao histórico.
   */
  emExperiencia: boolean = false,
  /**
   * Meta-alvo AGREGADA do gestor (supervisor/gerente) = soma, por consultor, da
   * meta da equipe dele (já = meta da equipe × nº de consultores da equipe).
   * Quando fornecida, substitui o alvo da meta no check bateuMeta SEM aplicar o
   * ×nVendedores de novo (já vem somado). A FAIXA/% e a regra de experiência
   * continuam idênticas (o multiplicador_n segue valendo só pra faixa).
   * `null` = comportamento histórico (meta do cargo × multiplicador).
   */
  metaOverride: MetaAgregadaGestor | null = null,
  renovacaoItens: ItemComRenovacaoDelta[] = [],
  /** Segmento da pessoa: escolhe a tabela de % do CPF ou do CNPJ (Douglas 21/10/2026). */
  segmento: 'PF' | 'PJ' = 'PJ',
  /**
   * TT e Renovação da regra 3 (Migração). No gestor vêm sem a Diretoria
   * (Douglas 29/10/2026); omitido, usa `quantidadesMeta`.
   */
  quantidadesMigracao?: QuantidadesMetas,
) {
  const { data: rules, isLoading: rulesLoading } = useCommissionRules(cargo, competencia, usuarioId, segmento);
  const ruleIds = useMemo(() => (rules || []).map((rule) => rule.id), [rules]);
  const { data: matrizRenovacao = [], isLoading: matrizRenovacaoLoading } = useCommissionRulesRenovacaoDelta(ruleIds);
  // Meta mensal (nova) — tem prioridade; também suporta individual por usuarioId
  const { data: metaMensal, isLoading: metaMensalLoading } = useMetaMensal(cargo, competencia, usuarioId);
  // Fallback legado
  const { data: metasConfig, isLoading: metasLegadoLoading } = useMetasConfig(cargo);

  const resultado = useMemo((): ResultadoComissao | null => {
    if (!rules || rules.length === 0) return null;

    // Para o check bateuMeta, usar quantidades/faturamentos META quando fornecidos (inclui DIRETORIA)
    const qMeta = quantidadesMeta ?? quantidades;
    const fMeta = faturamentosMeta ?? faturamentos;

    // Preferir meta mensal, fallback para legado
    let metaAltas = 0, metaFibras = 0;
    let metaFatVn = 0, metaFatTotal = 0, metaQtdTt = 0, metaQtdMp = 0, metaQtdMigracao = 0;
    let metaFatTi = 0, metaFatAvancada = 0;
    let metaQtdSd = 0;
    let metaQtdTv = 0;
    let usarAltas = false, usarFibras = false;
    let usarFatVn = false, usarFatTotal = false, usarQtdTt = false, usarQtdMp = false, usarQtdMigracao = false;
    let usarFatTi = false, usarFatAvancada = false;
    let usarQtdSd = false;
    let usarQtdTv = false;
    let multiplicadorN = false;

    if (metaMensal) {
      metaAltas    = metaMensal.meta_altas;
      metaFibras   = metaMensal.meta_fibras;
      metaFatVn    = metaMensal.meta_fat_vn;
      metaFatTotal = metaMensal.meta_fat_total;
      metaQtdTt    = metaMensal.meta_qtd_tt;
      metaQtdMp    = metaMensal.meta_qtd_mp;
      metaQtdMigracao = metaMensal.meta_qtd_migracao ?? 0;
      usarAltas    = metaMensal.usar_meta_altas;
      usarFibras   = metaMensal.usar_meta_fibras;
      usarFatVn    = metaMensal.usar_meta_fat_vn;
      usarFatTotal = metaMensal.usar_meta_fat_total;
      usarQtdTt    = metaMensal.usar_meta_qtd_tt;
      usarQtdMp    = metaMensal.usar_meta_qtd_mp;
      usarQtdMigracao = metaMensal.usar_meta_qtd_migracao ?? false;
      // (Douglas 28/07) Metas de FATURAMENTO de T.I. e Fixa Avançada — em reais,
      // não em quantidade. `?? 0/false` porque a coluna pode não existir em
      // linhas gravadas antes da migration.
      metaFatTi = Number(metaMensal.meta_fat_ti ?? 0);
      usarFatTi = metaMensal.usar_meta_fat_ti ?? false;
      metaFatAvancada = Number(metaMensal.meta_fat_avancada ?? 0);
      usarFatAvancada = metaMensal.usar_meta_fat_avancada ?? false;
      // (Douglas 29/08) Meta de SD (licenças). `?? 0/false` — linhas antigas
      // sem a coluna; nasce desligada, nada muda até ligarem no editor.
      metaQtdSd = Number((metaMensal as { meta_sd?: number }).meta_sd ?? 0);
      usarQtdSd = (metaMensal as { usar_meta_sd?: boolean }).usar_meta_sd ?? false;
      // (Douglas 21/10/2026) Meta de TV CPF (quantidade). Só existe no CPF.
      metaQtdTv = Number(metaMensal.meta_tv ?? 0);
      usarQtdTv = metaMensal.usar_meta_tv ?? false;
      multiplicadorN = metaMensal.multiplicador_n;
    } else if (metasConfig && metasConfig.length > 0) {
      // Fallback legado: sempre usa altas e fibras
      const meta = metasConfig[0];
      metaAltas    = meta.meta_altas;
      metaFibras   = meta.meta_fibras;
      usarAltas    = true;
      usarFibras   = true;
      multiplicadorN = meta.multiplicador_n;
    } else {
      // Sem meta configurada: passa direto sem bloquear
    }

    const useMultiplicador = multiplicadorN && nVendedores > 0;

    // 🆕 Gestor com meta agregada das equipes: o alvo já vem SOMADO por equipe
    // (meta da equipe × nº de consultores). Substituímos o alvo e as flags, e
    // NÃO aplicamos o ×nVendedores de novo. A faixa/% (abaixo) continua usando
    // o multiplicador normalmente. Sem override, comportamento histórico.
    if (metaOverride) {
      metaAltas = metaOverride.meta_altas;       metaFibras = metaOverride.meta_fibras;
      metaFatVn = metaOverride.meta_fat_vn;       metaFatTotal = metaOverride.meta_fat_total;
      metaQtdTt = metaOverride.meta_qtd_tt;       metaQtdMp = metaOverride.meta_qtd_mp;
      metaQtdMigracao = metaOverride.meta_qtd_migracao;
      usarAltas = metaOverride.usar_meta_altas;   usarFibras = metaOverride.usar_meta_fibras;
      usarFatVn = metaOverride.usar_meta_fat_vn;   usarFatTotal = metaOverride.usar_meta_fat_total;
      usarQtdTt = metaOverride.usar_meta_qtd_tt;   usarQtdMp = metaOverride.usar_meta_qtd_mp;
      usarQtdMigracao = metaOverride.usar_meta_qtd_migracao;
      metaFatTi = metaOverride.meta_fat_ti;        metaFatAvancada = metaOverride.meta_fat_avancada;
      usarFatTi = metaOverride.usar_meta_fat_ti;   usarFatAvancada = metaOverride.usar_meta_fat_avancada;
      metaQtdSd = metaOverride.meta_sd ?? 0;       usarQtdSd = metaOverride.usar_meta_sd ?? false;
      metaQtdTv = metaOverride.meta_tv ?? 0;       usarQtdTv = metaOverride.usar_meta_tv ?? false;
    }

    // Multiplicador aplicado AO ALVO da meta: só quando NÃO há override (override já soma).
    const useMultMeta = useMultiplicador && !metaOverride;
    const metaAltasFinal       = useMultMeta ? metaAltas       * nVendedores : metaAltas;
    const metaFibrasFinal      = useMultMeta ? metaFibras      * nVendedores : metaFibras;
    const metaQtdTtFinal       = useMultMeta ? metaQtdTt       * nVendedores : metaQtdTt;
    const metaQtdMpFinal       = useMultMeta ? metaQtdMp       * nVendedores : metaQtdMp;
    const metaQtdMigracaoFinal = useMultMeta ? metaQtdMigracao * nVendedores : metaQtdMigracao;
    const metaFatVnFinal       = useMultMeta ? metaFatVn       * nVendedores : metaFatVn;
    const metaFatTotalFinal    = useMultMeta ? metaFatTotal    * nVendedores : metaFatTotal;
    const metaFatTiFinal       = useMultMeta ? metaFatTi       * nVendedores : metaFatTi;
    const metaFatAvancadaFinal = useMultMeta ? metaFatAvancada * nVendedores : metaFatAvancada;
    const metaQtdSdFinal       = useMultMeta ? metaQtdSd       * nVendedores : metaQtdSd;
    const metaQtdTvFinal       = useMultMeta ? metaQtdTv       * nVendedores : metaQtdTv;

    // Encontrar faixa (mantém o multiplicador — % do gestor não muda)
    let faixaEnquadrada: number;
    if (useMultiplicador) {
      const { faixaBase } = encontrarFaixaSupervisor(faturamentos.total, nVendedores);
      faixaEnquadrada = faixaBase;
    } else {
      faixaEnquadrada = encontrarFaixa(faturamentos.total);
    }
    
    const regra = rules.find(r => r.faixa_faturamento === faixaEnquadrada);
    if (!regra) return null;

    // (Douglas 26/10/2026) Fibra a mais valida a Renovação. O número vem da linha
    // da própria pessoa; no gestor vale por consultor que conta no N, sobre o
    // alvo de fibra da equipe (Douglas 29/10/2026).
    const nFibrasRenovacao = (useMultiplicador || metaOverride) && nVendedores > 0 ? nVendedores : 1;
    const renovacaoPorFibra = usarQtdMp && renovacaoBatidaPorFibra({
      renovacao: qMeta.mp, metaRenovacao: metaQtdMpFinal,
      fibras: qMeta.fibras, metaFibras: metaFibrasFinal,
      regra: metaMensal ?? null,
      n: nFibrasRenovacao,
    });

    // bateuMeta: verifica apenas KPIs ativos — usa quantidades/faturamentos META (inclui DIRETORIA)
    const ttOk = !usarQtdTt || qMeta.tt >= metaQtdTtFinal;
    const renovacaoOk = !usarQtdMp || qMeta.mp >= metaQtdMpFinal || renovacaoPorFibra;
    const bateuSemTtRenovacao =
      (!usarAltas       || qMeta.altas    >= metaAltasFinal)        &&
      (!usarFibras      || qMeta.fibras   >= metaFibrasFinal)       &&
      (!usarFatVn       || (fMeta.pn + fMeta.ha) >= metaFatVnFinal) &&
      (!usarFatTotal    || fMeta.total   >= metaFatTotalFinal)     &&
      (!usarQtdMigracao || qMeta.migracao >= metaQtdMigracaoFinal)  &&
      // (Douglas 28/07) Metas de FATURAMENTO de T.I. / Fixa Avançada. Usam fMeta
      // (inclui DIRETORIA) igual às demais. Desligadas por padrão — mês nenhum
      // muda de comportamento até alguém ligar na tela.
      (!usarFatTi       || Number(fMeta.ti ?? 0)       >= metaFatTiFinal) &&
      (!usarFatAvancada || Number(fMeta.avancada ?? 0) >= metaFatAvancadaFinal) &&
      // (Douglas 29/08) SD por quantidade de licenças — mesma mecânica.
      (!usarQtdSd       || Number(qMeta.sd ?? 0)       >= metaQtdSdFinal) &&
      (!usarQtdTv       || Number(qMeta.tv ?? 0)       >= metaQtdTvFinal);
    const bateuSeparada = bateuSemTtRenovacao && ttOk && renovacaoOk;

    // (Douglas 29/10/2026) Regra 3: TT + Renovação juntos batem a meta e pagam
    // `pct_meta_migracao_junta` (50%). Só CNPJ; no gestor, sem a Diretoria.
    const regraMigracao = segmento === 'PJ' ? (metaMensal ?? null) : null;
    const qJunta = quantidadesMigracao ?? qMeta;
    const porMigracao = metaBatidaPorMigracao({
      regra: regraMigracao, bateuSeparada, bateuSemTtRenovacao,
      usarTt: usarQtdTt, usarRenovacao: usarQtdMp,
      tt: qJunta.tt, renovacao: qJunta.mp, metaTt: metaQtdTtFinal, metaRenovacao: metaQtdMpFinal,
    });
    const bateuMeta = bateuSeparada || porMigracao;
    const fatorMeta = porMigracao ? fatorMigracaoJunta(regraMigracao) : 1;

    const percentuais = {
      pn: regra.pn_pct,
      ha: regra.ha_pct,
      fibra: regra.fibra_pct,
      tt: regra.tt_pct,
      ttpre: regra.tt_pre_pct ?? regra.tt_pct,
      mp: regra.mp_pct,
      // (Douglas 28/07) T.I. e Fixa Avançada pagam como as demais categorias.
      // `?? 0` porque a coluna nasceu depois: regra antiga sem ela = não paga
      // (era exatamente isso que virava `R$ NaN` no detalhamento).
      ti: Number(regra.ti_pct ?? 0),
      avancada: Number(regra.avancada_pct ?? 0),
    };

    // Fora sul paga metade do % (Douglas 26/09/2026): a faixa e a meta acima
    // usam o faturamento inteiro; só a produtividade multiplica a base.
    const base = baseComissionavel(faturamentos);

    const renovacaoDelta = calcularComissaoRenovacaoDelta(
      base.mp,
      regra.id,
      percentuais.mp,
      matrizRenovacao,
      renovacaoItens,
    );

    const comissoes = {
      pn: (base.pn * percentuais.pn) / 100 * fatorMeta,
      ha: (base.ha * percentuais.ha) / 100 * fatorMeta,
      fibra: (base.fibra * percentuais.fibra) / 100 * fatorMeta,
      tt: (base.tt * percentuais.tt) / 100 * fatorMeta,
      ttpre: (base.ttpre * percentuais.ttpre) / 100 * fatorMeta,
      mp: renovacaoDelta.comissaoTotal * fatorMeta,
      ti: (base.ti * percentuais.ti) / 100 * fatorMeta,
      avancada: (base.avancada * percentuais.avancada) / 100 * fatorMeta,
      total: 0,
    };
    comissoes.total = comissoes.pn + comissoes.ha + comissoes.fibra + comissoes.tt
      + comissoes.ttpre + comissoes.mp + comissoes.ti + comissoes.avancada;

    // ============================================================
    // 🎓 REGRA MÍNIMA DE PERÍODO DE EXPERIÊNCIA — apenas CONSULTOR
    // ------------------------------------------------------------
    // Aditiva: nada do cálculo acima é alterado para outros casos.
    // Quando ativa: aplica os percentuais mínimos sobre o faturamento
    // ATIVO. Se bateu meta E o cálculo normal for maior, mantém o maior.
    // ============================================================
    let percentuaisFinal = percentuais;
    let comissoesFinal = comissoes;
    let bateuMetaFinal = bateuMeta;
    let regraExperiencia = false;

    if (emExperiencia && cargo === 'CONSULTOR') {
      const minimas = {
        pn:    (base.pn    * PERCENTUAIS_EXPERIENCIA.pn)    / 100,
        ha:    (base.ha    * PERCENTUAIS_EXPERIENCIA.ha)    / 100,
        fibra: (base.fibra * PERCENTUAIS_EXPERIENCIA.fibra) / 100,
        tt:    (base.tt    * PERCENTUAIS_EXPERIENCIA.tt)    / 100,
        ttpre: (base.ttpre * PERCENTUAIS_EXPERIENCIA.ttpre) / 100,
        mp:    (base.mp    * PERCENTUAIS_EXPERIENCIA.mp)    / 100,
        // T.I. e Fixa Avançada NÃO têm mínima de experiência definida (a régua
        // 50/50/50/10/10/10 é só das seis categorias antigas). Entram como 0 na
        // mínima: quem está em experiência e não bate meta não recebe por elas —
        // e quem bate leva o valor normal, pelo Math.max abaixo.
        ti: 0,
        avancada: 0,
        total: 0,
      };
      minimas.total = minimas.pn + minimas.ha + minimas.fibra + minimas.tt + minimas.ttpre + minimas.mp;

      if (bateuMeta) {
        // Bateu meta: protege o consultor — recebe o MAIOR entre normal e mínima por categoria
        comissoesFinal = {
          pn:    Math.max(comissoes.pn,    minimas.pn),
          ha:    Math.max(comissoes.ha,    minimas.ha),
          fibra: Math.max(comissoes.fibra, minimas.fibra),
          tt:    Math.max(comissoes.tt,    minimas.tt),
          ttpre: Math.max(comissoes.ttpre, minimas.ttpre),
          mp:    Math.max(comissoes.mp,    minimas.mp),
          ti:       comissoes.ti,
          avancada: comissoes.avancada,
          total: 0,
        };
        comissoesFinal.total =
          comissoesFinal.pn + comissoesFinal.ha + comissoesFinal.fibra +
          comissoesFinal.tt + comissoesFinal.ttpre + comissoesFinal.mp +
          comissoesFinal.ti + comissoesFinal.avancada;
        // Percentuais exibidos: o maior por categoria (informativo)
        percentuaisFinal = {
          pn:    Math.max(percentuais.pn,    PERCENTUAIS_EXPERIENCIA.pn),
          ha:    Math.max(percentuais.ha,    PERCENTUAIS_EXPERIENCIA.ha),
          fibra: Math.max(percentuais.fibra, PERCENTUAIS_EXPERIENCIA.fibra),
          tt:    Math.max(percentuais.tt,    PERCENTUAIS_EXPERIENCIA.tt),
          ttpre: Math.max(percentuais.ttpre, PERCENTUAIS_EXPERIENCIA.ttpre),
          mp:    Math.max(percentuais.mp,    PERCENTUAIS_EXPERIENCIA.mp),
          ti:       percentuais.ti,
          avancada: percentuais.avancada,
        };
      } else {
        // Não bateu meta: regra de experiência libera comissão pela mínima fixa
        comissoesFinal = minimas;
        percentuaisFinal = { ...PERCENTUAIS_EXPERIENCIA, ti: 0, avancada: 0 };
        bateuMetaFinal = true; // libera UI — comissão garantida
      }
      regraExperiencia = true;
    }

    const indexAtual = FAIXAS_FATURAMENTO.indexOf(faixaEnquadrada);
    const proximaFaixa = indexAtual < FAIXAS_FATURAMENTO.length - 1 
      ? FAIXAS_FATURAMENTO[indexAtual + 1] 
      : null;
    
    const proximaFaixaReal = proximaFaixa && useMultiplicador ? proximaFaixa * nVendedores : proximaFaixa;
    const faltaParaProximaFaixa = proximaFaixaReal ? proximaFaixaReal - faturamentos.total : 0;

    return {
      faturamentos,
      quantidades,
      // Inclui DIRETORIA — usado nos cards de progresso das metas
      quantidadesMeta: qMeta,
      faturamentosMeta: fMeta,
      faixaEnquadrada,
      percentuais: percentuaisFinal,
      comissoes: comissoesFinal,
      metas: {
        altas:          metaAltasFinal,
        fibras:         metaFibrasFinal,
        fat_vn:         metaFatVnFinal,
        fat_total:      metaFatTotalFinal,
        qtd_tt:         metaQtdTtFinal,
        qtd_mp:         metaQtdMpFinal,
        qtd_migracao:   metaQtdMigracaoFinal,
        qtd_sd:         metaQtdSdFinal,
        qtd_tv:         metaQtdTvFinal,
        fat_ti:         metaFatTiFinal,
        fat_avancada:   metaFatAvancadaFinal,
        usar_altas:     usarAltas,
        usar_fibras:    usarFibras,
        usar_fat_vn:    usarFatVn,
        usar_fat_total: usarFatTotal,
        usar_qtd_tt:    usarQtdTt,
        usar_qtd_mp:    usarQtdMp,
        usar_qtd_migracao: usarQtdMigracao,
        usar_qtd_sd:       usarQtdSd,
        usar_qtd_tv:       usarQtdTv,
        usar_fat_ti:       usarFatTi,
        usar_fat_avancada: usarFatAvancada,
        renovacao_por_fibra: renovacaoPorFibra,
        usar_migracao_junta: !!regraMigracao?.usar_meta_migracao_junta && usarQtdTt && usarQtdMp,
        migracao_junta: metaMigracaoJunta({ usarTt: usarQtdTt, usarRenovacao: usarQtdMp, metaTt: metaQtdTtFinal, metaRenovacao: metaQtdMpFinal }),
        migracao_junta_realizado: qJunta.tt + qJunta.mp,
        migracao_junta_pct: Math.round(fatorMigracaoJunta(regraMigracao) * 100),
        por_migracao: porMigracao,
      },
      bateuMeta: bateuMetaFinal,
      proximaFaixa: proximaFaixaReal,
      faltaParaProximaFaixa,
      nVendedores: useMultiplicador ? nVendedores : undefined,
      regraExperiencia,
      renovacaoDelta,
    };
  }, [rules, matrizRenovacao, metaMensal, metasConfig, faturamentos, quantidades, nVendedores, quantidadesMeta, faturamentosMeta, emExperiencia, cargo, metaOverride, renovacaoItens, segmento, quantidadesMigracao]);

  return {
    resultado,
    isLoading: rulesLoading || matrizRenovacaoLoading || metaMensalLoading || metasLegadoLoading,
  };
}
